import { useParams } from "react-router";
import { useEffect } from "react";
import "@xterm/xterm/css/xterm.css";

import { useTerminalTheme } from "../hooks/terminal/useTerminalTheme";
import { useTerminal } from "../hooks/terminal/useTerminal";
import { useTerminalConnection } from "../hooks/terminal/useTerminalConnection";
import { useSSHSessionWithErrorHandling } from "../hooks/terminal/useSSHSessionWithErrorHandling";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { SSHErrorCode } from "../types/ssh-errors";

import { TerminalHeader } from "../components/Terminal/TerminalHeader";
import { ShortcutsBar } from "../components/Terminal/ShortcutsBar";
import { SessionExpiredMessage } from "../components/Terminal/SessionExpiredMessage";
import { TerminalFooter } from "../components/Terminal/TerminalFooter";
import { styles, globalStyles } from "../components/Terminal/styles";
import { defaultShortcuts } from "../components/Terminal/shortcuts";

export const Terminal = () => {
  const sessionId = useParams<{ sessionId: string }>().sessionId;
  const { isDarkMode, toggleTheme } = useTerminalTheme();
  const isOnline = useOnlineStatus();

  // Always call all hooks unconditionally first
  const session = useSSHSessionWithErrorHandling(sessionId ?? "");
  const { terminalRef, xtermRef, isTerminalReady } = useTerminal({
    isDarkMode,
  });
  const {
    isConnected,
    isReconnecting,
    connectionError,
    errorCode,
    sendCommand,
    isCommandRunning,
    connectionStartTime,
    sessionClosed,
  } = useTerminalConnection({
    sessionId: sessionId ?? "",
    terminalRef: xtermRef,
    isSessionReady: session.isSuccess && isTerminalReady,
  });

  // Always render the terminal container, but show different content based on session state
  const projectDescription = session.data?.projectDescription ?? "";
  const targetName =
    session.data?.sessionType === "container"
      ? (session.data.containerServiceName ?? "")
      : (session.data?.appInstallationDescription ?? "");

  // Update document title with connection info - MUST be called before any early returns
  useEffect(() => {
    const baseTitle = "Web-SSH Terminal";

    if (session.isLoading) {
      document.title = `${baseTitle} - Verbinde...`;
    } else if (session.error?.message === "SESSION_NOT_FOUND") {
      document.title = `${baseTitle} - Session abgelaufen`;
    } else if (session.error) {
      document.title = `${baseTitle} - Fehler`;
    } else if (session.isSuccess) {
      const displayName = targetName || "Terminal";
      const projectName = projectDescription || "Unbekanntes Projekt";
      const status = isConnected
        ? "Verbunden"
        : isReconnecting
          ? "Verbinde neu..."
          : "Getrennt";

      document.title = `${displayName} (${projectName}) - ${status}`;
    } else {
      document.title = baseTitle;
    }

    // Cleanup: Reset title when component unmounts
    return () => {
      document.title = baseTitle;
    };
  }, [
    session.isLoading,
    session.error,
    session.isSuccess,
    targetName,
    projectDescription,
    isConnected,
    isReconnecting,
  ]);

  // Stop cursor blinking when session is closed
  useEffect(() => {
    if (
      (sessionClosed ||
        session.error?.message === "SESSION_NOT_FOUND" ||
        errorCode === SSHErrorCode.SESSION_NOT_FOUND) &&
      xtermRef.current
    ) {
      xtermRef.current.options.cursorBlink = false;
      xtermRef.current.blur();
    }
  }, [sessionClosed, session.error, errorCode, xtermRef]);

  // Early returns after all hooks have been called
  if (!sessionId) {
    return <p>Error: Session ID is missing.</p>;
  }

  // Don't return early for session errors - show overlay instead

  return (
    <div style={styles.container(isDarkMode)} className="terminal-container">
      <TerminalHeader
        appName={targetName || "Web-SSH Terminal"}
        projectName={
          session.isLoading ? "Lade..." : projectDescription || "Projekt"
        }
        isConnected={isConnected}
        isReconnecting={isReconnecting}
        connectionError={connectionError}
        errorCode={errorCode}
        sessionClosed={sessionClosed}
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
      />

      <ShortcutsBar
        shortcuts={defaultShortcuts}
        isConnected={isConnected}
        isDarkMode={isDarkMode}
        isCommandRunning={isCommandRunning}
        onExecute={sendCommand}
      />

      <div
        ref={terminalRef}
        style={styles.terminalContainer(isDarkMode)}
        onClick={() => {
          // Ensure terminal gets focus and cursor blinks when clicked
          if (xtermRef.current) {
            xtermRef.current.focus();
            xtermRef.current.options.cursorBlink = true;
          }
        }}
      />

      {session.isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: isDarkMode ? "#94a3b8" : "#6b7280",
            fontSize: "14px",
            zIndex: 10,
          }}
        >
          Session wird geladen...
        </div>
      )}

      {session.error && session.error.message !== "SESSION_NOT_FOUND" && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: isDarkMode ? "#ef4444" : "#dc2626",
            fontSize: "14px",
            zIndex: 10,
          }}
        >
          Fehler: {session.error.message}
        </div>
      )}

      <TerminalFooter
        isConnected={isConnected}
        isDarkMode={isDarkMode}
        sessionId={sessionId}
        projectName={projectDescription}
        connectionStartTime={connectionStartTime ?? undefined}
      />

      {/* Show SessionExpiredMessage as overlay when session is closed or not found */}
      {(session.error?.message === "SESSION_NOT_FOUND" ||
        sessionClosed ||
        errorCode === SSHErrorCode.SESSION_NOT_FOUND) && (
        <SessionExpiredMessage isDarkMode={isDarkMode} />
      )}

      <style>{globalStyles}</style>
    </div>
  );
};
